// Analytics helper for Daytona Virtual Store
// Pushes custom events to the GTM/GA4 dataLayer.

export type VirtualStoreEventCategory = string;
export type VirtualStoreEventName = 'click_virtualstore' | 'engagementtime_virtualstore' | 'exit_virtualstore' | string;

declare global {
  interface Window {
    // GTM dataLayer (usually initialized by the GTM snippet)
    dataLayer?: unknown[];
  }
}

const CLICK_EVENT_NAME: VirtualStoreEventName = 'click_virtualstore';
const ENGAGEMENT_EVENT_NAME: VirtualStoreEventName = 'engagementtime_virtualstore';
const EXIT_EVENT_NAME: VirtualStoreEventName = 'exit_virtualstore';

const DURATION_BUCKETS = [15, 30, 60, 120, 180, 300, 600, 900, 1200, 1800, 3600] as const;

const ensureDataLayer = (): unknown[] => {
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }
  return window.dataLayer;
};

const sanitizeParams = (params: Record<string, unknown>): Record<string, unknown> => {
  const cleaned: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v !== undefined) cleaned[k] = v;
  }
  return cleaned;
};

const normalizeLabel = (label: unknown): string => {
  const s = String(label ?? '').trim();
  return s.length > 0 ? s : '(not set)';
};

const normalizeDurationSec = (value: unknown): number => {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, n);
};

const toDurationBucket = (durationSec: number): string => {
  for (const bucket of DURATION_BUCKETS) {
    if (durationSec <= bucket) return `<=${bucket}`;
  }
  return `>${DURATION_BUCKETS[DURATION_BUCKETS.length - 1]}`;
};

export const pushToDataLayer = (payload: Record<string, unknown>) => {
  const dl = ensureDataLayer();
  dl.push(payload);
};

const pushEvent = (
  eventName: VirtualStoreEventName,
  event_category: VirtualStoreEventCategory,
  event_label: string,
  params: Record<string, unknown> = {}
) => {
  pushToDataLayer({
    event: eventName,
    event_category,
    event_label: normalizeLabel(event_label),
    // Useful for debugging / ordering
    event_ts: Date.now(),
    ...sanitizeParams(params),
  });
};

export const trackClick = (
  event_category: VirtualStoreEventCategory,
  event_label: string,
  params: Record<string, unknown> = {},
  eventName: VirtualStoreEventName = CLICK_EVENT_NAME
) => {
  // Avoid accidentally overriding required keys.
  const { event: _event, event_category: _cat, event_label: _label, ...rest } = params as any;
  pushEvent(eventName, event_category, event_label, rest);
};

export const trackEngagement = (
  event_category: VirtualStoreEventCategory,
  durationSec: number,
  event_label: string,
  params: Record<string, unknown> = {},
  eventName: VirtualStoreEventName = ENGAGEMENT_EVENT_NAME
) => {
  const sec = normalizeDurationSec(durationSec);
  if (sec < 1) return;

  const { event: _event, event_category: _cat, event_label: _label, engagement_time_sec: _dur, ...rest } = params as any;
  pushEvent(eventName, event_category, event_label, {
    engagement_time_sec: sec,
    duration_bucket: toDurationBucket(sec),
    ...rest,
  });
};

export const trackExit = (
  params: Record<string, unknown> = {},
  event_category: VirtualStoreEventCategory = 'exit',
  event_label = 'exit',
  eventName: VirtualStoreEventName = EXIT_EVENT_NAME
) => {
  const { event: _event, event_category: _cat, event_label: _label, ...rest } = params as any;
  pushEvent(eventName, event_category, event_label, rest);
};
